import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Provider, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { cloneDeep, flatten, isEmpty, isNull, isString, isUndefined, uniqBy } from "lodash";
import { TreeListEditControlComponent } from "rl-common/components/char-data/controls/tree-list-edit-control.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { LoaderComponent } from "rl-common/components/panel/loader/loader.component";
import { APKeyValueOption } from "rl-common/models/ap-key-value-option";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { ICharacteristicMetaDataValue } from "rl-common/models/i-characteristic-meta-data-value";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ICharacteristicValueGroup } from "rl-common/models/i-characteristic-value-group";
import { IInactiveCharacteristicMetaDataValue } from "rl-common/models/i-inactive-characteristic-meta-data-value";
import { CharTypeId } from "rl-common/rl-common.consts";
import { RuleSource } from "rl-common/services/accounting-processes/models/rule-source";
import { RuleType } from "rl-common/services/accounting-processes/models/rule-type";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { ICreateAccountingProcessAccountGroupRule } from "rl-common/services/accounting-processes/models/i-create-accounting-process-account-group-rule";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { SearchService } from "rl-common/services/search/search.service";
import { Subscription } from "rxjs";
import { map, tap } from "rxjs/operators";
import { AccountingProcesses } from "../accounting-processes.consts";
import { APContactTypeAheadComponent, ITypeAheadSelectedEvent } from "../ap-contact-type-ahead/ap-contact-type-ahead.component";
import { BuildAccountGroupRuleModalAdapter, IBuildAccountGroupRuleModalComponent } from "./build-account-group-rule-modal.adapter";
import { IBuildAccountGroupRuleResult } from "./build-account-group-rule-modal.models";

export interface IAccountGroupRuleForm {
	ruleSource: FormControl<APKeyValueOption>;
	ruleType: FormControl<APKeyValueOption>;
	picklist: FormControl<ICharacteristicMetaData>;
	/** Contact source: single-select, preserving the pre-Rule-Set behavior. */
	picklistValue: FormControl<APKeyValueOption>;
	/** Amount source: tree multi-select, holding LOV branch ids. */
	picklistValues: FormControl<number[]>;
	contactRecord: FormControl<APKeyValueOption>;
	template: FormControl<ICharacteristicTemplate>;
	templateGroup: FormControl<APKeyValueOption>;
}

export interface IEditRule {
	picklist: ICharacteristicMetaData;
	picklistValue: APKeyValueOption;
	picklistValues: number[];
	contactRecord: APKeyValueOption;
	template: ICharacteristicTemplate;
	templateGroup: APKeyValueOption;

}

@Component({
	selector: "rl-build-account-group-rule-modal",
	templateUrl: "./build-account-group-rule-modal.component.html",
	styleUrls: ["./build-account-group-rule-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormTableRowComponent, FormTableControlDirective, DropdownSingleComponent, ReactiveFormsModule, APContactTypeAheadComponent, LoaderComponent, TreeListEditControlComponent]
})
export class BuildAccountGroupRuleModalComponent implements OnInit, OnDestroy, IBuildAccountGroupRuleModalComponent {
	private readonly _fb = inject(FormBuilder);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _entityConfigService = inject(EntityConfigService);
	private readonly _searchService = inject(SearchService);


	@Input()
	accountGroup: IAccountingProcessAccountGroup;

	/** rules belong to a Rule Set; the Set carries the account and the sequence number */
	@Input()
	ruleSet: IAccountingProcessAccountGroupRuleSet;

	@Input()
	accountGroupRule: ICreateAccountingProcessAccountGroupRule;

	@Output()
	onClose = new EventEmitter<IBuildAccountGroupRuleResult>();

	formReady: boolean = false;
	isNewAccountGroupRule: boolean;
	pageTitle: string;

	contactCharTypeId = CharTypeId.User;

	form: FormGroup<IAccountGroupRuleForm>;

	/** the enum itself, so the template can compare against RuleType.* instead of magic numbers */
	readonly ruleTypes = RuleType;

	get isAmountSource() {
		return this.form?.controls.ruleSource.value?.id === RuleSource.Amount;
	}

	ruleSourceItems: APKeyValueOption[] = [
		{ id: RuleSource.Contact, value: "Contact" },
		{ id: RuleSource.Amount, value: "Amount" }
	];
	ruleSourceOptions: DropdownOptions<APKeyValueOption> = {
		items: this.ruleSourceItems,
		rowKeyFn: (source: APKeyValueOption) => source.id,
		rowLabelFn: (source: APKeyValueOption) => source.value
	};

	ruleTypeItems: APKeyValueOption[] = [
		{ id: RuleType.FieldValue, value: "Field Value" },
		{ id: RuleType.Record, value: "Record" },
		{ id: RuleType.Template, value: "Template" },
		{ id: RuleType.TemplateGroup, value: "Template Group" }
	];
	ruleTypeOptions: DropdownOptions<APKeyValueOption> = {
		items: this.ruleTypeItems,
		rowKeyFn: (type: APKeyValueOption) => type.id,
		rowLabelFn: (type: APKeyValueOption) => type.value
	};

	/** contact-side picklists, off the templates the Account Group's party can use */
	contactPicklistItems: ICharacteristicMetaData[] = [];

	/** amount-side picklists, restricted to the three whitelisted fields (see the consts) */
	amountPicklistItems: ICharacteristicMetaData[] = [];

	/**
	 * Keyed by characteristicID, NOT charValueSetID: a value set is the shared LOV, so several fields
	 * reference the same one (Country, Citizenship and Organized In all point at the countries set).
	 * The dropdown resolves a selection with items.find(rowKeyFn(item) === selectedKey), so a shared
	 * key silently hands back the first field with that set -- pick "Country" and get "Organized In".
	 */
	triggerPicklistOptions: DropdownOptions<ICharacteristicMetaData> = {
		items: [],
		rowKeyFn: (type: ICharacteristicMetaData) => type.characteristicID,
		rowLabelFn: (type: ICharacteristicMetaData) => type.label
	};

	// the tree control's LOV inputs, refreshed whenever the selected picklist changes
	lovs: ICharacteristicMetaDataValue[] = [];
	inactiveLovs: IInactiveCharacteristicMetaDataValue[] = [];
	charValueGroups: ICharacteristicValueGroup[] = [];
	charValueGroupAssociations: { [key: number]: string[] } = {};

	triggerPicklistValueItems: APKeyValueOption[] = [];
	triggerPicklistValueOptions: DropdownOptions<APKeyValueOption> = {
		items: this.triggerPicklistValueItems,
		rowKeyFn: (type: APKeyValueOption) => type.id,
		rowLabelFn: (type: APKeyValueOption) => type.value
	};

	triggerTemplateItems: ICharacteristicTemplate[] = [];
	triggerTemplateOptions: DropdownOptions<ICharacteristicTemplate> = {
		items: this.triggerTemplateItems,
		rowKeyFn: (template: ICharacteristicTemplate) => template.templateID,
		rowLabelFn: (template: ICharacteristicTemplate) => template.templateID + " - " + template.templateName
	};

	triggerTemplateGroupItems: APKeyValueOption[] = [];
	triggerTemplateGroupOptions: DropdownOptions<APKeyValueOption> = {
		items: this.triggerTemplateGroupItems,
		rowKeyFn: (type: APKeyValueOption) => type.id,
		rowLabelFn: (type: APKeyValueOption) => type.value
	};

	private readonly _subs: Subscription[] = [];

	ngOnInit(): void {
		this.isNewAccountGroupRule = !this.accountGroupRule?.groupRuleId;

		this.pageTitle = this.isNewAccountGroupRule
			? `Add New Rule to Rule Set: <strong>${this.ruleSet.name}</strong>`
			: `Edit Rule on Rule Set: <strong>${this.ruleSet.name}</strong>`;

		this.buildAccountGroupRuleForm();
	}

	private async buildAccountGroupRuleForm() {
		const rule = this.isNewAccountGroupRule ? null : cloneDeep(this.accountGroupRule);
		const ruleType: RuleType = rule?.ruleType ?? null;
		// new rules default to Contact, matching the column default and the pre-Rule-Set flow
		const ruleSource: RuleSource = rule?.ruleSource ?? RuleSource.Contact;

		const editRule = {
			picklist: null,
			picklistValue: null,
			picklistValues: [],
			contactRecord: null,
			template: null,
			templateGroup: null
		} as IEditRule;

		if (rule) {
			if (ruleType === RuleType.FieldValue) {
				// a stub for now -- the real cmd is swapped in below, once the picklist items load
				editRule.picklist = {
					characteristicID: rule.characteristicId,
					charValueSetID: rule.charValueSetId,
					charValueSourceID: rule.charValueSourceId
				} as ICharacteristicMetaData;

				// Contact rules are single-select, so they carry exactly one value; Amount rules hold
				// the whole branch id list. Either way the column is int[].
				editRule.picklistValues = rule.characteristicValueIds ?? [];
				editRule.picklistValue = editRule.picklistValues.length
					? { id: editRule.picklistValues[0], value: null }
					: null;
			}

			if (ruleType === RuleType.Record) {
				editRule.contactRecord = rule.recordId === 0 ? null : { id: rule.recordId, value: await this.getContactName(rule.recordId) };
			}

			if (ruleType === RuleType.Template) {
				editRule.template = { templateID: rule.templateId } as ICharacteristicTemplate;
			}

			if (ruleType === RuleType.TemplateGroup) {
				editRule.templateGroup = rule.templateGroupId === 0 ? null : { id: rule.templateGroupId, value: null };
			}
		}

		this.form = this._fb.group<IAccountGroupRuleForm>({
			ruleSource: this._fb.control(this.ruleSourceItems.find(rs => rs.id === ruleSource), { validators: Validators.required }),
			ruleType: this._fb.control(ruleType === null ? null : { id: ruleType, value: null }, { validators: Validators.required }),
			picklist: this._fb.control(editRule.picklist),
			picklistValue: this._fb.control(editRule.picklistValue),
			picklistValues: this._fb.control(editRule.picklistValues),
			contactRecord: this._fb.control(editRule.contactRecord),
			template: this._fb.control(editRule.template),
			templateGroup: this._fb.control(editRule.templateGroup)
		}, { validators: [ruleFieldsRequiredValidator] });

		const ruleSourceSub = this.form.controls.ruleSource.valueChanges.subscribe(source => this.applyRuleSource(source?.id as RuleSource));
		const picklistSub = this.form.controls.picklist.valueChanges.subscribe(cmd => this.applyPicklist(cmd));

		await this._entityConfigService.getPartyTemplates(CharTypeId.Deal, this.accountGroup.dealTemplates[0].templateID, this.accountGroup.partyId).pipe(
			map(entities => {
				return entities.map(e => this._oneConfig.getTemplate(e.charTypeId, e.templateId));
			}),
			map(templates => {
				const templateItems = templates.filter(x => !!x).sort((a, b) => a.templateName.localeCompare(b.templateName));
				this.triggerTemplateItems = templateItems;
				this.triggerTemplateOptions.items = this.triggerTemplateItems;
				return templateItems;
			}),
			tap(templateItems => {
				const charCollection = templateItems.map(ti => this._oneConfig.getTemplateMetaData(CharTypeId.User, ti.templateID));
				this.contactPicklistItems = uniqBy(flatten(charCollection.map(c => c.characteristicMetaDatas.filter(cmd => AccountingProcesses.CharDataValueMapping.isLov(cmd)))), "characteristicID").sort((a, b) => a.label.localeCompare(b.label));
			})
		).toPromise();

		await this._entityConfigService.getTemplateGroupList(CharTypeId.User).pipe(
			map(contactTemplateGroups => {
				return contactTemplateGroups.map(ctg => AccountingProcesses.KeyValueOptionMapping.fromTemplateGroup(ctg));
			}),
			tap(contactTemplateGroupKeyValueOptions => {
				this.triggerTemplateGroupItems = contactTemplateGroupKeyValueOptions.sort((a, b) => a.value.localeCompare(b.value));
				this.triggerTemplateGroupOptions.items = this.triggerTemplateGroupItems;
			})
		).toPromise();

		// Amount fields ride along in the OneConfig payload, so no fetch -- just the whitelist filter
		this.amountPicklistItems = AccountingProcesses.amountRuleCmds(this._oneConfig)
			.sort((a, b) => a.label.localeCompare(b.label));

		// Read the source off the control rather than reusing the local: the modal body renders for a
		// new rule before the fetches above resolve, so the user can already have switched to Amount.
		// Re-applying the rule's original source would point the picklist at the other source's items
		// -- and leave the Rule Type unlocked -- behind their back.
		const currentSource = this.form.controls.ruleSource.value?.id as RuleSource;
		this.applyRuleSource(currentSource, { keepSelection: true });

		// Now that the real cmds are loaded, replace the stub so the dropdown renders its label and
		// the tree gets a resolvable charValueSourceID. Silent: applyPicklist runs by hand below,
		// because letting valueChanges fire here would blank the values just restored from the rule.
		if (ruleType === RuleType.FieldValue) {
			const items = currentSource === RuleSource.Amount ? this.amountPicklistItems : this.contactPicklistItems;
			// Resolve the field by its own id whenever the rule carries one. Legacy rules (saved before
			// XCHR_ID existed) only recorded the value set, and several fields share one, so all that
			// fallback can do is pick the first field on the set -- the same thing the grid label does.
			const cmd = rule.characteristicId
				? items.find(p => p.characteristicID === rule.characteristicId)
				: items.find(p => p.charValueSetID === rule.charValueSetId);
			if (cmd) {
				this.form.controls.picklist.setValue(cmd, { emitEvent: false });
			}
			this.applyPicklist(this.form.controls.picklist.value, { keepSelection: true });
		}

		this.formReady = true;

		this._subs.push(ruleSourceSub, picklistSub);
	}

	/**
	 * Swaps in the picklists for the chosen source. Amount rules only support Field Value -- there is
	 * no Amount record, template or template group to match on -- so the Rule Type control is locked
	 * rather than offering types the evaluator could never satisfy.
	 */
	private applyRuleSource(ruleSource: RuleSource, opts: { keepSelection?: boolean } = {}) {
		this.triggerPicklistOptions.items = ruleSource === RuleSource.Amount
			? this.amountPicklistItems
			: this.contactPicklistItems;

		if (ruleSource === RuleSource.Amount) {
			this.form.controls.ruleType.setValue(this.ruleTypeItems.find(rt => rt.id === RuleType.FieldValue), { emitEvent: false });
			this.form.controls.ruleType.disable({ emitEvent: false });
		} else {
			this.form.controls.ruleType.enable({ emitEvent: false });
		}

		// changing source invalidates the field, since the two sources share no value sets
		if (!opts.keepSelection) {
			this.form.controls.picklist.setValue(null);
		}
	}

	/** Feeds whichever value control the current source uses: tree for Amount, dropdown for Contact. */
	private applyPicklist(cmd: ICharacteristicMetaData, opts: { keepSelection?: boolean } = {}) {
		if (this.isAmountSource) {
			const lmd = this._oneConfig.getLovMetaData(cmd?.charValueSourceID);
			this.lovs = lmd?.listOfValues ?? [];
			this.inactiveLovs = lmd?.inactiveListOfValues ?? [];
			this.charValueGroups = lmd?.charValueGroups ?? [];
			this.charValueGroupAssociations = lmd?.charValueGroupAssociations ?? {};
			if (!opts.keepSelection) {
				this.form.controls.picklistValues.setValue([]);
			}
		} else {
			this.getTriggerPicklistValueItems(cmd);
			if (opts.keepSelection) {
				// the restored value is an {id, value: null} stub; swap in the real item so the
				// dropdown has a label to render instead of showing blank
				const selectedId = this.form.controls.picklistValue.value?.id;
				const item = this.triggerPicklistValueItems.find(pv => pv.id === selectedId);
				if (item) {
					this.form.controls.picklistValue.setValue(item, { emitEvent: false });
				}
			} else {
				this.form.controls.picklistValue.setValue(this.triggerPicklistValueItems[0]);
			}
		}
	}

	getContactName(recordId: number) {
		return this._searchService.getTitles(CharTypeId.User, [recordId]).pipe(
			map(result => {
				return result.get(recordId);
			})
		).toPromise();
	}

	getTriggerPicklistValueItems(cmd: ICharacteristicMetaData) {
		// Never leave this undefined: the picklist valueChanges handler reads [0] off it straight
		// away, and the optional chain below short-circuits whenever the LOV has no values (or the
		// picklist was cleared) -- which threw on an undefined index instead of just emptying the list.
		this.triggerPicklistValueItems = this._oneConfig.getLovMetaData(cmd?.charValueSourceID)?.listOfValues
			?.map(v => AccountingProcesses.KeyValueOptionMapping.fromCharacteristicMetaDataValue(v))
			.sort((a, b) => a.value.localeCompare(b.value)) ?? [];
		this.triggerPicklistValueOptions.items = this.triggerPicklistValueItems;
	}

	contactRecordTypeAheadAdd($event: ITypeAheadSelectedEvent) {
		this.form.controls.contactRecord.setValue({ id: $event.recordId, value: $event.title } as APKeyValueOption);
		this.form.markAsDirty();
	}

	clearContactRecord() {
		this.form.controls.contactRecord.setValue(null);
	}

	save() {
		if (!this.form.valid) {
			return;
		}
		// get account group rule from form
		const selectedRuleType = this.form.controls.ruleType.value.id as RuleType;

		const baseRule: Partial<ICreateAccountingProcessAccountGroupRule> = {
			groupRuleId: this.accountGroupRule?.groupRuleId,
			groupRuleSetId: this.ruleSet.groupRuleSetId,
			ruleSource: this.form.controls.ruleSource.value.id,
			ruleType: this.form.controls.ruleType.value.id
		};

		let additionalRule: Partial<ICreateAccountingProcessAccountGroupRule>;

		if (selectedRuleType === RuleType.FieldValue) {
			additionalRule = {
				// The field itself, not just its value set: a value set is a shared LOV, so saving only
				// that leaves the chosen field unrecoverable wherever siblings share it (Citizenship,
				// Country and Organized In all point at the countries set).
				characteristicId: this.form.controls.picklist.value.characteristicID,
				charValueSetId: this.form.controls.picklist.value.charValueSetID,
				charValueSourceId: this.form.controls.picklist.value.charValueSourceID,
				// one column either way: Contact contributes its single selection as a 1-length array
				characteristicValueIds: this.isAmountSource
					? this.form.controls.picklistValues.value
					: [this.form.controls.picklistValue.value.id]
			};
		}

		if (selectedRuleType === RuleType.Record) {
			additionalRule = {
				recordId: this.form.controls.contactRecord.value.id
			};
		}

		if (selectedRuleType === RuleType.Template) {
			additionalRule = {
				templateId: this.form.controls.template.value.templateID
			};
		}

		if (selectedRuleType === RuleType.TemplateGroup) {
			additionalRule = {
				templateGroupId: this.form.controls.templateGroup.value.id
			};
		}

		this.accountGroupRule = { ...baseRule, ...additionalRule } as ICreateAccountingProcessAccountGroupRule;

		this.onClose.next({
			accountGroupRule: this.accountGroupRule
		});
	}

	close() {
		this.onClose.next(null);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}

}

export const ruleFieldsRequiredValidator: ValidatorFn = (form: FormGroup<IAccountGroupRuleForm>): ValidationErrors | null => {
	const ruleType = form?.controls?.ruleType?.value?.id as RuleType;

	const requiredValues: unknown[] = [];
	switch (ruleType) {
		case RuleType.FieldValue:
			requiredValues.push(form.controls.picklist.value);
			// the two sources fill different controls, and an empty array is neither null nor an empty
			// string, so the filter below would wave it through -- collapse it to null here instead
			if (form?.controls?.ruleSource?.value?.id === RuleSource.Amount) {
				requiredValues.push(form.controls.picklistValues.value?.length ? form.controls.picklistValues.value : null);
			} else {
				requiredValues.push(form.controls.picklistValue.value);
			}
			break;
		case RuleType.Record:
			requiredValues.push(form.controls.contactRecord.value);
			break;
		case RuleType.Template:
			requiredValues.push(form.controls.template.value);
			break;
		case RuleType.TemplateGroup:
			requiredValues.push(form.controls.templateGroup.value);
			break;
	}

	const missingValues = requiredValues.filter(val => isNull(val) || isUndefined(val) || (isString(val) && isEmpty(val)));
	if (!isEmpty(missingValues)) {
		return { requiredFieldsMissing: true };
	}

	return null;
};

export const BUILDACCOUNTGROUPRULE_MODAL_PROVIDER: Provider = {
	provide: BuildAccountGroupRuleModalAdapter,
	useFactory: () => new BuildAccountGroupRuleModalAdapter(BuildAccountGroupRuleModalComponent)
};
